import { dayjs, getAppTimezone } from '@lib/dayjs'

export type DateFormat =
  | 'short'
  | 'shortDate'
  | 'full'
  | 'dateAndTime'
  | 'fullMonth'
  | 'slashTime'
  | 'timeOnly'
  | 'dayMonthTime'
  | 'fullWithSeconds'
  | 'timeShort'
  | 'fullWithZone'
  | 'isoDate'
  | 'isoMinutes'

export const dateFormats: Record<DateFormat, string> = {
  full: 'MMM DD, YYYY, h:mm A', // Jul 18, 2024, 9:28 PM
  short: 'MMM D, YYYY', // Jul 18, 2024
  shortDate: 'DD/MM/YYYY', // 18/07/2024
  dateAndTime: 'MMM D, h:mm A', // Jul 18, 9:28 PM
  fullMonth: 'MMMM DD, YYYY', // July 18, 2024
  slashTime: 'MM/DD, HH:mm:ss', // 07/18, 21:28:00
  timeOnly: 'h:mm:ss A', // 9:28 PM
  timeShort: 'hh:mm A', // 04:07 PM
  dayMonthTime: 'DD MMM HH:mm', // 18 Jul 21:28
  fullWithSeconds: 'MMM DD, YYYY, h:mm:ss A', // Jul 18, 2024, 9:28 PM
  fullWithZone: 'MMM DD, YYYY, h:mm:ss A z', // Jul 18, 2024, 9:28:00 PM GMT+4
  isoDate: 'YYYY-MM-DD', // 2024-07-18
  isoMinutes: 'YYYY-MM-DD HH:mm', // 2024-07-18 21:28
}

export const UTC_TIMEZONE = 'UTC'

export const formatDate = (
  date: string | null | undefined,
  format: DateFormat = 'full',
  // Defaults to the timezone the app runs in; pass `UTC_TIMEZONE` to render the raw server instant.
  timeZone: string = getAppTimezone(),
) => {
  if (!date) return null

  return dayjs.utc(date).tz(timeZone).format(dateFormats[format])
}

export const formatDateUtc = (date: string | null | undefined, format: DateFormat = 'full') =>
  formatDate(date, format, UTC_TIMEZONE)

// '1990-07-18' => 35 (years), null when date is missing or in the future
export const getAge = (date: string | null | undefined) => {
  if (!date) return null

  const birthDate = dayjs.utc(date)
  // diff truncates, so a date under a year ahead would read as age 0.
  if (birthDate.isAfter(dayjs())) return null

  return dayjs().diff(birthDate, 'year')
}

// 204800 => 200.0 KB, 5242880 => 5.0 MB
const units = ['KB', 'MB', 'GB']
export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`

  let size = bytes / 1024
  let unit = 0

  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }

  return `${size.toFixed(1)} ${units[unit]}`
}

// 1234.4324112 => 1,234.43
export const formatNumber = (amount: number | string) =>
  Number(amount).toLocaleString('en-us', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
